// The Helmr import template, as data: a UI renders it and a downloadable CSV header comes from it.

export type EntityName = "members" | "memberships" | "dues" | "pt_balances" | "leads" | "staff" | "device_users";
export type FieldType = "string" | "phone" | "email" | "date" | "paise" | "int" | "enum";

export interface FieldDef {
  name: string;
  label: string;
  type: FieldType;
  required: boolean;
  description: string;
  examples: string[];
  /** Normalised (lowercase, space-separated) header synonyms used by the heuristic mapper. */
  synonyms: string[];
  enumValues?: string[];
  /** alias (lowercase) -> canonical enum value */
  enumAliases?: Record<string, string>;
  /** Inclusive bounds for int / paise values. */
  min?: number;
  max?: number;
  maxLength?: number;
  /** Regex source the normalised string must match. */
  pattern?: string;
  isPersonName?: boolean;
}

export interface EntityDef {
  name: EntityName;
  label: string;
  description: string;
  fields: FieldDef[];
}

/** Order operations are applied in a dry run / cutover. */
export const IMPORT_ORDER: EntityName[] = ["staff", "members", "memberships", "pt_balances", "dues", "device_users", "leads"];

const PHONE_SYN = [
  "phone", "phone no", "phone number", "mobile", "mobile no", "mobile number", "mob", "mob no", "contact",
  "contact no", "contact number", "cell", "cell no", "whatsapp", "whatsapp no", "whatsapp number", "ph no", "telephone",
];

const MAX_PAISE = 10_000_000_000; // ₹10 crore sanity ceiling

const phone = (name: string, label: string, required: boolean, desc: string): FieldDef => ({
  name, label, type: "phone", required, description: desc, examples: ["9876543210", "+91 98765 43210"], synonyms: PHONE_SYN,
});

const memberPhone: FieldDef = phone(
  "member_phone", "Member phone", true,
  "Phone of the member this row belongs to. Inherited from the member row when the sheet is one wide table.",
);

export const ENTITIES: EntityDef[] = [
  {
    name: "members",
    label: "Members",
    description: "One row per person. Phone is the identity key.",
    fields: [
      { name: "full_name", label: "Full name", type: "string", required: true, isPersonName: true, maxLength: 120,
        description: "Member's name.", examples: ["Asha Verma", "Rohit Nair"],
        synonyms: ["name", "member name", "full name", "customer name", "client name", "member", "name of member", "student name"] },
      phone("phone", "Phone", true, "Primary mobile number. Normalised to E.164 using the business's default country."),
      { name: "email", label: "Email", type: "email", required: false, description: "Email address.", examples: ["asha@example.com"],
        synonyms: ["email", "email id", "e mail", "mail id", "email address"] },
      { name: "gender", label: "Gender", type: "enum", required: false, description: "male / female / other.", examples: ["female", "M"],
        enumValues: ["male", "female", "other"], enumAliases: { m: "male", f: "female", man: "male", woman: "female", o: "other" },
        synonyms: ["gender", "sex"] },
      { name: "dob", label: "Date of birth", type: "date", required: false, description: "Date of birth.", examples: ["14/03/1992"],
        synonyms: ["dob", "date of birth", "birth date", "birthday", "d o b"] },
      { name: "joined_on", label: "Joined on", type: "date", required: false, description: "Original joining date.", examples: ["02-Jan-2023"],
        synonyms: ["doj", "date of joining", "joining date", "join date", "joined on", "joined", "admission date", "registration date", "reg date"] },
      { name: "external_id", label: "Old member ID", type: "string", required: false, maxLength: 60,
        description: "Member ID in the previous software, kept for reference.", examples: ["M-0042"],
        synonyms: ["member id", "member no", "member code", "id", "reg no", "registration no", "code"] },
      { name: "address", label: "Address", type: "string", required: false, maxLength: 300, description: "Postal address.", examples: ["12 MG Road"],
        synonyms: ["address", "addr"] },
      { name: "notes", label: "Notes", type: "string", required: false, maxLength: 500, description: "Free-text remarks.", examples: ["Knee injury"],
        synonyms: ["notes", "remarks", "comment", "comments", "note"] },
    ],
  },
  {
    name: "memberships",
    label: "Active memberships",
    description: "The member's current plan with start and end dates.",
    fields: [
      memberPhone,
      { name: "plan_name", label: "Plan", type: "string", required: true, maxLength: 120, description: "Plan / package name as the gym knows it.",
        examples: ["Quarterly", "Annual Gold"],
        synonyms: ["plan", "plan name", "package", "pkg", "pkg name", "package name", "membership", "membership type", "membership plan", "scheme"] },
      { name: "start_date", label: "Start date", type: "date", required: true, description: "Plan start (business-local date).", examples: ["01/04/2025"],
        synonyms: ["start date", "start", "from", "from date", "plan start", "membership start", "begin date", "commencement"] },
      { name: "end_date", label: "End date", type: "date", required: true, description: "Plan end / expiry (business-local date).", examples: ["30/06/2025"],
        synonyms: ["end date", "end", "expiry", "expiry date", "exp date", "expires", "valid till", "valid upto", "valid up to", "to date", "till", "plan end", "membership end"] },
      { name: "amount_paid", label: "Amount paid", type: "paise", required: false, min: 0, max: MAX_PAISE,
        description: "Amount paid for this plan, in rupees in the sheet (stored as integer paise).", examples: ["₹7,500", "7500.00"],
        synonyms: ["amount paid", "paid amt", "paid amount", "paid", "fees paid", "fee paid"] },
    ],
  },
  {
    name: "dues",
    label: "Dues",
    description: "Outstanding balances owed by members.",
    fields: [
      memberPhone,
      { name: "amount", label: "Amount due", type: "paise", required: true, max: MAX_PAISE,
        description: "Outstanding amount in rupees (stored as integer paise). Ambiguous forms like 1.2k are rejected.", examples: ["₹1,500", "2500.50"],
        synonyms: ["amt due", "amount due", "due", "due amount", "dues", "balance", "pending", "pending amount", "outstanding", "balance due", "pending fees"] },
      { name: "due_date", label: "Due date", type: "date", required: false, description: "Date the amount was/is due.", examples: ["15/05/2025"],
        synonyms: ["due date", "payment due date", "due on", "next due date"] },
      { name: "note", label: "Note", type: "string", required: false, maxLength: 300, description: "Remark about the due.", examples: ["Promised by Friday"],
        synonyms: ["due note", "due remarks"] },
    ],
  },
  {
    name: "pt_balances",
    label: "PT balances",
    description: "Personal-training packs with sessions remaining.",
    fields: [
      memberPhone,
      { name: "pack_name", label: "PT pack", type: "string", required: true, maxLength: 120, description: "Pack name.", examples: ["12-session PT"],
        synonyms: ["pt package", "pt pack", "pt plan", "pack", "pack name", "training package", "personal training", "pt"] },
      { name: "sessions_total", label: "Sessions total", type: "int", required: true, min: 1, max: 1000, description: "Sessions bought.", examples: ["12"],
        synonyms: ["total sessions", "sessions", "no of sessions", "pt sessions", "sessions total", "total pt sessions", "sessions purchased"] },
      { name: "sessions_used", label: "Sessions used", type: "int", required: true, min: 0, max: 1000, description: "Sessions already consumed.", examples: ["5"],
        synonyms: ["used sessions", "sessions used", "sessions completed", "completed sessions", "consumed", "sessions consumed", "attended sessions"] },
      { name: "pack_price_ex_tax", label: "PT pack price before tax", type: "paise", required: false, min: 0, max: MAX_PAISE,
        description: "What the member paid for the pack before tax, in rupees (stored as integer paise). D50 pays the trainer per session from this price; without it every imported session earns 0.",
        examples: ["₹12,000", "12000.00"],
        synonyms: ["pt price", "pt pack price", "pack price", "pt amount", "pt fee", "pt fees", "pack amount", "pt pack amount", "training fee", "training fees", "pack price before tax"] },
      { name: "trainer_name", label: "Trainer", type: "string", required: false, isPersonName: true, maxLength: 120,
        description: "Trainer's name; matched against staff.", examples: ["Vikram"],
        synonyms: ["trainer", "trainer name", "coach", "coach name", "pt trainer", "trainer assigned", "assigned trainer", "instructor"] },
    ],
  },
  {
    name: "leads",
    label: "Leads",
    description: "Enquiries that have not joined yet.",
    fields: [
      { name: "full_name", label: "Full name", type: "string", required: true, isPersonName: true, maxLength: 120, description: "Lead's name.", examples: ["Neha Iyer"],
        synonyms: ["name", "lead name", "full name", "enquiry name", "inquiry name", "prospect"] },
      phone("phone", "Phone", true, "Lead's mobile number."),
      { name: "email", label: "Email", type: "email", required: false, description: "Email address.", examples: ["neha@example.com"],
        synonyms: ["email", "email id", "e mail", "mail id", "email address"] },
      { name: "source", label: "Source", type: "string", required: false, maxLength: 60, description: "Where the lead came from.", examples: ["Instagram", "Walk-in"],
        synonyms: ["source", "lead source", "enquiry source", "channel", "referral"] },
      { name: "status", label: "Status", type: "enum", required: false, description: "Lead stage.", examples: ["new", "trial"],
        enumValues: ["new", "contacted", "trial", "won", "lost"],
        enumAliases: { enquiry: "new", open: "new", "follow up": "contacted", followup: "contacted", "trial booked": "trial", joined: "won", converted: "won", closed: "lost", dead: "lost" },
        synonyms: ["status", "lead status", "stage"] },
      { name: "interested_in", label: "Interested in", type: "string", required: false, maxLength: 120, description: "Plan or goal they asked about.", examples: ["Weight loss"],
        synonyms: ["interested in", "interest", "enquiry for", "enquiry about", "interested plan", "goal"] },
      { name: "follow_up_on", label: "Follow up on", type: "date", required: false, description: "Next follow-up date.", examples: ["10/06/2025"],
        synonyms: ["follow up", "follow up date", "followup date", "next follow up", "next followup", "callback date"] },
      { name: "created_on", label: "Enquiry date", type: "date", required: false, description: "When the enquiry came in.", examples: ["01/06/2025"],
        synonyms: ["enquiry date", "lead date", "inquiry date", "enquired on", "date", "created on"] },
      { name: "notes", label: "Notes", type: "string", required: false, maxLength: 500, description: "Remarks.", examples: ["Call after 6pm"],
        synonyms: ["notes", "remarks", "comment", "comments", "note"] },
    ],
  },
  {
    name: "staff",
    label: "Staff",
    description: "Trainers, front desk, managers.",
    fields: [
      { name: "full_name", label: "Full name", type: "string", required: true, isPersonName: true, maxLength: 120, description: "Staff member's name.", examples: ["Vikram Singh"],
        synonyms: ["name", "staff name", "employee name", "emp name", "full name"] },
      phone("phone", "Phone", true, "Staff mobile number (used for login)."),
      { name: "email", label: "Email", type: "email", required: false, description: "Email address.", examples: ["vikram@example.com"],
        synonyms: ["email", "email id", "e mail", "mail id", "email address"] },
      { name: "role_name", label: "Role", type: "string", required: true, maxLength: 60, description: "Role as named in the gym (matched to Helmr roles at cutover).", examples: ["Trainer", "Front desk"],
        synonyms: ["role", "designation", "position", "job title"] },
      { name: "joined_on", label: "Joined on", type: "date", required: false, description: "Joining date.", examples: ["01/01/2022"],
        synonyms: ["doj", "date of joining", "joining date", "join date", "joined on", "joined"] },
    ],
  },
  {
    name: "device_users",
    label: "Biometric device users",
    description: "ESSL/ZKTeco device user IDs mapped to members (no re-enrolment). UID 999 is reserved.",
    fields: [
      { name: "device_uid", label: "Device UID", type: "int", required: true, min: 1, max: 65535, description: "User ID on the biometric terminal. 999 is reserved.", examples: ["17"],
        synonyms: ["biometric id", "bio id", "device uid", "device id", "uid", "user id", "machine id", "punch id", "enroll id", "enrollment no", "biometric no", "bio metric id", "essl id", "zk id", "fingerprint id", "finger id"] },
      memberPhone,
      { name: "device_serial", label: "Device serial", type: "string", required: false, maxLength: 60, description: "Terminal serial; UIDs are unique per terminal. Blank = the single/default terminal.", examples: ["CKPG1234567"],
        synonyms: ["device serial", "machine serial", "device sn", "device", "machine"] },
    ],
  },
];

export const ENTITY_MAP: Record<EntityName, EntityDef> = Object.fromEntries(ENTITIES.map((e) => [e.name, e])) as Record<EntityName, EntityDef>;

export function getEntity(name: EntityName): EntityDef {
  return ENTITY_MAP[name];
}

export function getField(entity: EntityName, field: string): FieldDef | undefined {
  return ENTITY_MAP[entity].fields.find((f) => f.name === field);
}

/** Header row for a downloadable CSV template. */
export function templateHeader(entity: EntityName): string[] {
  return ENTITY_MAP[entity].fields.map((f) => f.label);
}

function csvCell(v: string): string {
  return /[",\n\r]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}

/** Downloadable CSV template: header row plus one example row. */
export function templateCsv(entity: EntityName): string {
  const def = ENTITY_MAP[entity];
  return [def.fields.map((f) => f.label), def.fields.map((f) => f.examples[0] ?? "")].map((r) => r.map(csvCell).join(",")).join("\r\n") + "\r\n";
}

/** Sheets that carry several entities in one wide table are entered as `members`. */
export function entitiesForSheet(entity: EntityName): EntityName[] {
  return entity === "members" ? ["members", "memberships", "pt_balances", "dues", "device_users"] : [entity];
}
